import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  IconButton,
  Typography,
  CircularProgress,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormLabel,
  FormHelperText,
  Switch,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { createNews, News } from '../../services/newsService';
import { getClientVariables, ClientVariable } from '../../services/promotionService';
import { uploadImage } from '../../firebase';
import { useSnackbar } from 'notistack';
import ImageUpload from '../common/ImageUpload';

interface AddNewsDialogProps {
  open: boolean;
  onClose: (created?: boolean) => void;
  onNewsCreated: (news: News) => void;
}

type RecipientType = 'segment' | 'client';

const defaultFormData = {
  title: '',
  description: '',
  img: '',
  recipientType: 'segment' as RecipientType,
  channel: 'TODOS',
  gec: 'TODOS',
  clientIds: '',
  status: 'activo' as 'activo' | 'inactivo',
};

const AddNewsDialog: React.FC<AddNewsDialogProps> = ({ open, onClose, onNewsCreated }) => {
  const [formData, setFormData] = useState<typeof defaultFormData>(defaultFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<{image?: string}>({});
  const [clientVariables, setClientVariables] = useState<{ channels: ClientVariable[]; gec: ClientVariable[] }>({ channels: [], gec: [] });
  const [loadingVariables, setLoadingVariables] = useState(true);
  const { enqueueSnackbar } = useSnackbar();

  // Load client variables when dialog opens
  useEffect(() => {
    const loadClientVariables = async () => {
      if (open) {
        try {
          setLoadingVariables(true);
          console.log('Loading client variables for news...');
          const data = await getClientVariables();
          console.log('Client variables loaded:', data);
          setClientVariables(data);
        } catch (error) {
          console.error('Error loading client variables:', error);
          enqueueSnackbar('Error al cargar las variables del cliente', { variant: 'error' });
        } finally {
          setLoadingVariables(false);
        }
      }
    };

    loadClientVariables();
  }, [open, enqueueSnackbar]);

  const handleStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    setFormData(prev => ({
      ...prev,
      status: checked ? 'activo' : 'inactivo'
    }));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { name: string; value: string } }) => {
    const { name, value } = e.target;
    
    setFormData(prev => {
      // Si cambia el tipo de destinatario, limpiamos los campos que no correspondan
      if (name === 'recipientType') {
        return {
          ...prev,
          recipientType: value as RecipientType,
          channel: value === 'segment' ? prev.channel : 'TODOS',
          gec: value === 'segment' ? prev.gec : 'TODOS',
          clientIds: value === 'client' ? prev.clientIds : ''
        };
      }
      
      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const handleImageChange = useCallback((file: File | null) => {
    setImageFile(file);
    
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          img: reader.result as string
        }));
      };
      reader.readAsDataURL(file);
      
      // Clear image error if there was one
      if (errors.image) {
        setErrors(prev => ({
          ...prev,
          image: ''
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        img: ''
      }));
    }
  }, [errors]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title || !formData.description) {
      enqueueSnackbar('Por favor complete todos los campos requeridos', { variant: 'error' });
      return;
    }

    // Validar que si es por cliente, se hayan ingresado IDs
    if (formData.recipientType === 'client' && !formData.clientIds.trim()) {
      enqueueSnackbar('Por favor ingrese al menos un ID de cliente', { variant: 'error' });
      return;
    }
    
    // Validar que se haya subido una imagen
    if (!imageFile && !formData.img) {
      setErrors(prev => ({ ...prev, image: 'Debes subir una imagen para la novedad' }));
      return;
    }

    setIsSubmitting(true);

    try {
      let imgUrl = formData.img;
      
      // Subir imagen si hay un archivo nuevo
      if (imageFile) {
        const timestamp = Date.now();
        const fileExtension = imageFile.name.split('.').pop();
        const fileName = `news_${timestamp}.${fileExtension}`;
        const storagePath = `news/${fileName}`;
        
        enqueueSnackbar('Subiendo imagen...', { variant: 'info' });
        imgUrl = await uploadImage(imageFile, storagePath);
      }

      // Preparar los datos según el tipo de destinatario
      const newsData = {
        title: formData.title,
        description: formData.description,
        img: imgUrl,
        recipientType: formData.recipientType,
        ...(formData.recipientType === 'segment' 
          ? { 
              channel: formData.channel,
              gec: formData.gec,
              recipients: ''
            } 
          : { 
              channel: 'TODOS',
              gec: 'TODOS',
              recipients: formData.clientIds.split(',').map(id => id.trim()).join(',')
            })
      };

      // Llamar a la API para crear la noticia
      const newNews = await createNews(newsData);
      
      // Notificar éxito
      enqueueSnackbar('Novedad creada exitosamente', { variant: 'success' });
      
      // Cerrar el diálogo y notificar al componente padre
      onNewsCreated(newNews);
      handleClose(true);
      
    } catch (error) {
      console.error('Error al crear la novedad:', error);
      enqueueSnackbar('Error al crear la novedad', { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = (created = false) => {
    if (!isSubmitting) {
      setFormData(defaultFormData);
      setImageFile(null);
      onClose(created);
    }
  };

  return (
    <Dialog open={open} onClose={() => handleClose()} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Nueva Novedad</Typography>
          <IconButton onClick={() => handleClose()} disabled={isSubmitting}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, p: 2 }}>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label="Título"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                margin="normal"
                required
                disabled={isSubmitting}
              />
              <TextField
                fullWidth
                label="Descripción"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                margin="normal"
                multiline
                rows={4}
                required
                disabled={isSubmitting}
              />
              <FormControl component="fieldset" margin="normal">
                <FormLabel component="legend">Tipo de Destinatarios</FormLabel>
                <RadioGroup
                  row
                  name="recipientType"
                  value={formData.recipientType}
                  onChange={handleInputChange}
                >
                  <FormControlLabel 
                    value="segment" 
                    control={<Radio />} 
                    label="Por Segmento" 
                    disabled={isSubmitting}
                  />
                  <FormControlLabel 
                    value="client" 
                    control={<Radio />} 
                    label="Por Cliente" 
                    disabled={isSubmitting}
                  />
                </RadioGroup>
              </FormControl>

              {formData.recipientType === 'segment' ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
                  {loadingVariables && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="caption" color="textSecondary">
                        Cargando variables del cliente...
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                    <FormControl margin="normal" disabled={isSubmitting || loadingVariables} sx={{ flex: 1 }}>
                      <InputLabel id="channel-label">Canal</InputLabel>
                      <Select
                        labelId="channel-label"
                        name="channel"
                        value={formData.channel}
                        onChange={handleInputChange}
                        label="Canal"
                        fullWidth
                      >
                        <MenuItem value="TODOS">Todos</MenuItem>
                        {clientVariables.channels.map((channel) => (
                          <MenuItem key={channel.id} value={channel.id}>
                            {channel.id}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl margin="normal" disabled={isSubmitting || loadingVariables} sx={{ flex: 1 }}>
                      <InputLabel id="gec-label">GEC</InputLabel>
                      <Select
                        labelId="gec-label"
                        name="gec"
                        value={formData.gec}
                        onChange={handleInputChange}
                        label="GEC"
                        fullWidth
                      >
                        <MenuItem value="TODOS">Todos</MenuItem>
                        {clientVariables.gec.map((gec) => (
                          <MenuItem key={gec.id} value={gec.id}>
                            {gec.id}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              ) : (
                <TextField
                  fullWidth
                  label="IDs de Clientes"
                  name="clientIds"
                  value={formData.clientIds}
                  onChange={handleInputChange}
                  margin="normal"
                  helperText="Ingrese los IDs de los clientes separados por comas"
                  disabled={isSubmitting}
                  placeholder="Ej: 12345, 67890, 54321"
                />
              )}
            </Box>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%',
                mb: 2
              }}>
                <Typography variant="subtitle2" gutterBottom>
                  Imagen de la Novedad
                </Typography>
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <ImageUpload
                    onChange={handleImageChange}
                    label="Subir imagen"
                    disabled={isSubmitting}
                    previewSize={{ width: 188, height: 188 }}
                  />
                  <Box sx={{ mt: 0.5, textAlign: 'center' }}>
                    <Typography variant="caption" color="textSecondary" display="block" sx={{ fontSize: '0.75rem' }}>
                      Formatos permitidos: webp, png, jpg
                    </Typography>
                    <Typography variant="caption" color="textSecondary" display="block" sx={{ fontSize: '0.75rem' }}>
                      Peso máximo: 1MB
                    </Typography>
                  </Box>
                </Box>
                {errors.image && (
                  <FormHelperText error sx={{ mt: 1, alignSelf: 'flex-start' }}>
                    {errors.image}
                  </FormHelperText>
                )}
                
                <Box mt={3} width="100%" textAlign="center">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.status === 'activo'}
                        onChange={handleStatusChange}
                        color="success"
                      />
                    }
                    label={formData.status === 'activo' ? 'Activo' : 'Inactivo'}
                    labelPlacement="start"
                    sx={{ 
                      margin: 0,
                      '& .MuiFormControlLabel-label': {
                        marginLeft: 1,
                        color: 'text.primary',
                        fontWeight: 500,
                      },
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: 'success.main',
                        '&:hover': {
                          backgroundColor: 'rgba(46, 125, 50, 0.04)',
                        },
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: 'success.main',
                        opacity: 0.8,
                      },
                      '& .MuiSwitch-track': {
                        backgroundColor: 'grey.400',
                        opacity: 0.8,
                      },
                    }}
                  />
                </Box>
              </Box>
              <Box sx={{ width: '100%', display: 'flex', gap: 2, mt: 'auto' }}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={isSubmitting}
                  onClick={handleSubmit}
                  sx={{ mt: 2, flex: 1 }}
                >
                  {isSubmitting ? <CircularProgress size={24} /> : 'Guardar'}
                </Button>
                <Button
                  variant="outlined"
                  color="inherit"
                  fullWidth
                  disabled={isSubmitting}
                  onClick={() => handleClose()}
                  sx={{ 
                    mt: 2,
                    flex: 1,
                    color: 'text.primary',
                    borderColor: 'text.secondary',
                    '&:hover': {
                      borderColor: 'text.primary',
                      backgroundColor: 'action.hover'
                    }
                  }}
                >
                  Cancelar
                </Button>
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </form>
    </Dialog>
  );
};

export default AddNewsDialog;
